import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, BookOpen, Building2, ChevronDown, ChevronUp, Search } from 'lucide-react'
import { Badge, Button, Card } from '@/components/ui'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import { formatPartnerDisplay } from '@/lib/utils'
import {
  addShmpMember,
  cancelScheduledShmpChange,
  excludeShmpMember,
  getShmpMembershipHistory,
  listShmpCandidates,
  listShmpMembers,
  listScheduledShmpAdditions,
  listShmpWeekStarts,
  transferShmpMember,
  type ShmpWeekStart,
} from '@/services/shmpService'
import { useAuthStore } from '@/store'
import type {
  ShmpCandidateRow,
  ShmpCourseNumber,
  ShmpMemberRow,
  ShmpMembershipHistoryRow,
  ShmpScheduledAdditionRow,
} from '@/types'

const COURSE_OPTIONS: ShmpCourseNumber[] = [1, 2, 3]

function todayIso(): string {
  const date = new Date()
  const offset = date.getTimezoneOffset()
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10)
}

function formatWeekDate(date: string): string {
  const [year, month, day] = date.split('-')
  return year && month && day ? `${day}.${month}.${year}` : date
}

function formatWeekOption(week: ShmpWeekStart): string {
  return `Неделя ${week.week_number_in_period}: ${formatWeekDate(week.week_start)} - ${formatWeekDate(week.week_end)}`
}

export function ShmpMembersPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const [members, setMembers] = useState<ShmpMemberRow[]>([])
  const [candidates, setCandidates] = useState<ShmpCandidateRow[]>([])
  const [scheduledAdditions, setScheduledAdditions] = useState<ShmpScheduledAdditionRow[]>([])
  const [weekStarts, setWeekStarts] = useState<ShmpWeekStart[]>([])
  const [loading, setLoading] = useState(true)
  const [isAdvanced, setIsAdvanced] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyPartner, setBusyPartner] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [courseFilter, setCourseFilter] = useState<'all' | ShmpCourseNumber>('all')
  const [addPartnerNumber, setAddPartnerNumber] = useState('')
  const [selectedCandidate, setSelectedCandidate] = useState<ShmpCandidateRow | null>(null)
  const [candidateSearch, setCandidateSearch] = useState('')
  const [addCourse, setAddCourse] = useState<ShmpCourseNumber>(1)
  const [addDate, setAddDate] = useState('')
  const [reason, setReason] = useState('')
  const [expandedPartner, setExpandedPartner] = useState<number | null>(null)
  const [history, setHistory] = useState<Record<number, ShmpMembershipHistoryRow[]>>({})

  const reload = async () => {
    setLoading(true)
    try {
      const [memberRows, scheduledRows, weeks] = await Promise.all([
        listShmpMembers(),
        listScheduledShmpAdditions(),
        listShmpWeekStarts(),
      ])
      setMembers(memberRows)
      setScheduledAdditions(scheduledRows)
      setWeekStarts(weeks)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  useEffect(() => {
    let cancelled = false
    const timer = window.setTimeout(() => {
      listShmpCandidates(candidateSearch)
        .then(rows => {
          if (!cancelled) setCandidates(rows)
        })
        .catch(err => {
          if (!cancelled) setError(err instanceof Error ? err.message : String(err))
        })
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [candidateSearch, members])

  useEffect(() => {
    let cancelled = false
    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => { cancelled = true }
  }, [getAccessLevel])

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase()
    return members.filter(member => {
      if (courseFilter !== 'all' && member.course_number !== courseFilter) return false
      if (!query) return true
      return member.partner_number.toString().includes(query)
        || member.persons_fio?.toLowerCase().includes(query)
        || member.cities_text?.toLowerCase().includes(query)
    })
  }, [courseFilter, members, search])

  const stats = useMemo(() => COURSE_OPTIONS.map(course => ({
    course,
    partners: members.filter(member => member.course_number === course).length,
    studios: members
      .filter(member => member.course_number === course)
      .reduce((sum, member) => sum + member.studio_count, 0),
  })), [members])

  const transferDates = useMemo(() => {
    const today = todayIso()
    return weekStarts
      .filter(week => week.week_start >= today)
      .sort((a, b) => a.week_start.localeCompare(b.week_start))
      .slice(0, 12)
  }, [weekStarts])

  const addWeekOptions = useMemo(() => {
    const today = todayIso()
    const currentWeek = weekStarts.find(week =>
      week.week_start <= today && week.week_end >= today
    )
    const futureWeeks = weekStarts
      .filter(week => week.week_start > today)
      .sort((a, b) => a.week_start.localeCompare(b.week_start))
    const pastWeeks = weekStarts
      .filter(week => week.week_end < today)
      .sort((a, b) => b.week_start.localeCompare(a.week_start))

    return [
      ...(currentWeek ? [currentWeek] : []),
      ...futureWeeks,
      ...pastWeeks,
    ]
  }, [weekStarts])

  useEffect(() => {
    if (addDate || addWeekOptions.length === 0) return
    setAddDate(addWeekOptions[0].week_start)
  }, [addDate, addWeekOptions])

  const handleAdd = async () => {
    const partnerNumber = Number(addPartnerNumber)
    if (!Number.isInteger(partnerNumber) || !addDate) return
    setBusyPartner(partnerNumber)
    try {
      await addShmpMember({
        partnerNumber,
        courseNumber: addCourse,
        enrolledFrom: addDate,
        reason,
      })
      setAddPartnerNumber('')
      setSelectedCandidate(null)
      setCandidateSearch('')
      setReason('')
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyPartner(null)
    }
  }

  const handleTransfer = async (
    member: ShmpMemberRow,
    newCourseNumber: ShmpCourseNumber,
    effectiveFrom: string,
    transferReason: string
  ) => {
    if (!effectiveFrom || newCourseNumber === member.course_number) return
    setBusyPartner(member.partner_number)
    try {
      await transferShmpMember({
        partnerNumber: member.partner_number,
        newCourseNumber,
        effectiveFrom,
        reason: transferReason,
      })
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyPartner(null)
    }
  }

  const handleExclude = async (
    member: ShmpMemberRow,
    effectiveTo: string,
    exclusionReason: string
  ) => {
    setBusyPartner(member.partner_number)
    try {
      await excludeShmpMember({
        membershipId: member.membership_id,
        effectiveTo,
        reason: exclusionReason,
      })
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyPartner(null)
    }
  }

  const handleCancelScheduled = async (member: ShmpMemberRow) => {
    if (!member.scheduled_membership_id || !member.scheduled_from) return
    const confirmed = window.confirm(
      `Отменить запланированный переход на ${member.scheduled_from}?`
    )
    if (!confirmed) return
    setBusyPartner(member.partner_number)
    try {
      await cancelScheduledShmpChange({
        membershipId: member.scheduled_membership_id,
        reason: 'Отменено пользователем',
      })
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyPartner(null)
    }
  }

  const handleCancelScheduledAddition = async (row: ShmpScheduledAdditionRow) => {
    const confirmed = window.confirm(
      `Отменить зачисление франчайзи ${row.partner_number} на ${row.enrolled_from}?`
    )
    if (!confirmed) return
    setBusyPartner(row.partner_number)
    try {
      await cancelScheduledShmpChange({
        membershipId: row.membership_id,
        reason: 'Запланированное зачисление отменено пользователем',
      })
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyPartner(null)
    }
  }

  const toggleHistory = async (partnerNumber: number) => {
    if (expandedPartner === partnerNumber) {
      setExpandedPartner(null)
      return
    }
    setExpandedPartner(partnerNumber)
    if (history[partnerNumber]) return
    try {
      const rows = await getShmpMembershipHistory(partnerNumber)
      setHistory(current => ({ ...current, [partnerNumber]: rows }))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <BookOpen size={22} className="text-primary" />
          <h1 className="text-xl font-semibold">ШМП</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Состав участников и переходы между курсами
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {stats.map(item => (
          <Card key={item.course}>
            <div className="text-sm text-muted-foreground">{item.course} курс</div>
            <div className="text-2xl font-semibold mt-1">{item.partners}</div>
            <div className="text-xs text-muted-foreground">{item.studios} студий</div>
          </Card>
        ))}
      </div>

      {isAdvanced && (
        <Card>
          <div className="font-semibold mb-3">Добавить участника</div>
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_auto_auto] gap-2">
            <ShmpCandidatePicker
              options={candidates}
              value={addPartnerNumber}
              selectedOption={selectedCandidate}
              query={candidateSearch}
              onQueryChange={setCandidateSearch}
              onChange={candidate => {
                setAddPartnerNumber(String(candidate.partner_number))
                setSelectedCandidate(candidate)
              }}
            />
            <select
              value={addCourse}
              onChange={event => setAddCourse(Number(event.target.value) as ShmpCourseNumber)}
              className="px-3 py-2 border border-border rounded-md bg-background text-sm"
            >
              {COURSE_OPTIONS.map(course => (
                <option key={course} value={course}>{course} курс</option>
              ))}
            </select>
            <select
              value={addDate}
              onChange={event => setAddDate(event.target.value)}
              className="px-3 py-2 border border-border rounded-md bg-background text-sm"
            >
              <option value="">Выберите неделю начала</option>
              {addWeekOptions.map(week => (
                <option key={week.week_id} value={week.week_start}>
                  {formatWeekOption(week)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <input
              value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder="Комментарий или причина"
              className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-sm"
            />
            <Button
              size="sm"
              disabled={!addPartnerNumber || !addDate || busyPartner !== null}
              onClick={handleAdd}
            >
              Добавить
            </Button>
          </div>
        </Card>
      )}

      {scheduledAdditions.length > 0 && (
        <Card>
          <div className="font-semibold mb-2">Запланированные зачисления</div>
          <div className="space-y-2">
            {scheduledAdditions.map(row => (
              <div
                key={row.membership_id}
                className="flex flex-col sm:flex-row sm:items-center gap-2 border-t border-border pt-2 first:border-0 first:pt-0"
              >
                <div className="flex-1 text-sm">
                  {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
                  <span className="text-muted-foreground">
                    {' '}· {row.course_number} курс с {row.enrolled_from}
                  </span>
                </div>
                {isAdvanced && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyPartner === row.partner_number}
                    onClick={() => handleCancelScheduledAddition(row)}
                  >
                    Отменить
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Поиск по ФИО, городу или номеру франчайзи"
            className="w-full pl-9 pr-3 py-2 border border-border rounded-md bg-background text-sm"
          />
        </div>
        <select
          value={courseFilter}
          onChange={event => setCourseFilter(
            event.target.value === 'all'
              ? 'all'
              : Number(event.target.value) as ShmpCourseNumber
          )}
          className="px-3 py-2 border border-border rounded-md bg-background text-sm"
        >
          <option value="all">Все курсы</option>
          {COURSE_OPTIONS.map(course => (
            <option key={course} value={course}>{course} курс</option>
          ))}
        </select>
      </div>

      {error && (
        <Card className="border border-error/30">
          <div className="text-sm text-error">{error}</div>
        </Card>
      )}

      {loading ? (
        <Card><div className="text-sm text-muted-foreground">Загрузка...</div></Card>
      ) : (
        <div className="space-y-2">
          {filteredMembers.map(member => (
            <MemberCard
              key={member.membership_id}
              member={member}
              isAdvanced={isAdvanced}
              busy={busyPartner === member.partner_number}
              transferDates={transferDates}
              expanded={expandedPartner === member.partner_number}
              history={history[member.partner_number] ?? []}
              onTransfer={handleTransfer}
              onExclude={handleExclude}
              onCancelScheduled={handleCancelScheduled}
              onToggleHistory={toggleHistory}
              onOpenAnalytics={partnerNumber => navigate(`/partners/${partnerNumber}/analytics`)}
            />
          ))}
          {filteredMembers.length === 0 && (
            <Card><div className="text-sm text-muted-foreground">Участники не найдены.</div></Card>
          )}
        </div>
      )}
    </div>
  )
}

function ShmpCandidatePicker(props: {
  options: ShmpCandidateRow[]
  value: string
  selectedOption: ShmpCandidateRow | null
  query: string
  onQueryChange: (value: string) => void
  onChange: (candidate: ShmpCandidateRow) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        props.onQueryChange('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [props.onQueryChange])

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => {
          setIsOpen(open => !open)
          if (isOpen) props.onQueryChange('')
        }}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-left text-sm"
      >
        <span className={props.selectedOption ? 'min-w-0 truncate' : 'text-muted-foreground'}>
          {props.selectedOption
            ? formatPartnerDisplay(
                props.selectedOption.partner_number,
                props.selectedOption.persons_fio,
                props.selectedOption.cities_text
              )
            : 'Выберите франчайзи'}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[320px] overflow-hidden rounded-lg border border-border bg-card shadow-lg">
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md bg-muted/50 px-2 py-2">
              <Search size={15} className="shrink-0 text-muted-foreground" />
              <input
                value={props.query}
                onChange={event => props.onQueryChange(event.target.value)}
                placeholder="Поиск по ФИО, городу или номеру"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {props.options.map(candidate => {
              const selected = props.value === String(candidate.partner_number)
              return (
                <button
                  key={candidate.partner_number}
                  type="button"
                  onClick={() => {
                    props.onChange(candidate)
                    props.onQueryChange('')
                    setIsOpen(false)
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ${
                    selected ? 'bg-primary/10' : 'hover:bg-muted/50'
                  }`}
                >
                  <Building2
                    size={16}
                    className={selected ? 'shrink-0 text-primary' : 'shrink-0 text-muted-foreground'}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm font-medium ${selected ? 'text-primary' : ''}`}>
                      {candidate.persons_fio || `Франчайзи №${candidate.partner_number}`}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {candidate.cities_text || 'Город не указан'} · №{candidate.partner_number} · {candidate.studio_count} студ.
                    </span>
                  </span>
                  {selected && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                </button>
              )
            })}
            {props.options.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                Франчайзи с активными студиями не найдены.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function MemberCard(props: {
  member: ShmpMemberRow
  isAdvanced: boolean
  busy: boolean
  transferDates: ShmpWeekStart[]
  expanded: boolean
  history: ShmpMembershipHistoryRow[]
  onTransfer: (
    member: ShmpMemberRow,
    course: ShmpCourseNumber,
    effectiveFrom: string,
    reason: string
  ) => Promise<void>
  onExclude: (member: ShmpMemberRow, effectiveTo: string, reason: string) => Promise<void>
  onCancelScheduled: (member: ShmpMemberRow) => Promise<void>
  onToggleHistory: (partnerNumber: number) => Promise<void>
  onOpenAnalytics: (partnerNumber: number) => void
}) {
  const { member } = props
  const [newCourse, setNewCourse] = useState<ShmpCourseNumber>(
    member.course_number === 3 ? 2 : member.course_number + 1 as ShmpCourseNumber
  )
  const [transferDate, setTransferDate] = useState('')
  const [transferReason, setTransferReason] = useState('')
  const [showExclude, setShowExclude] = useState(false)
  const [excludeDate, setExcludeDate] = useState(todayIso)
  const [excludeReason, setExcludeReason] = useState('')

  return (
    <Card>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">
              {formatPartnerDisplay(
                member.partner_number,
                member.persons_fio,
                member.cities_text
              )}
            </span>
            <Badge text={`${member.course_number} курс`} variant="warning" />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {member.studio_count} студий · в курсе с {member.enrolled_from}
          </div>
          {member.studio_count === 0 && (
            <div className="flex items-center gap-1 text-xs text-warning mt-1">
              <AlertTriangle size={13} />
              Нет активных студий
            </div>
          )}
          {member.scheduled_membership_id && (
            <div className="text-xs text-primary mt-1">
              Запланирован {member.scheduled_course_number} курс с {member.scheduled_from}
              {props.isAdvanced && (
                <button
                  type="button"
                  disabled={props.busy}
                  onClick={() => props.onCancelScheduled(member)}
                  className="ml-2 underline hover:no-underline disabled:opacity-50"
                >
                  Отменить
                </button>
              )}
            </div>
          )}
        </div>

        {props.isAdvanced && (
          <div className="flex flex-wrap gap-2">
            <select
              value={newCourse}
              onChange={event => setNewCourse(Number(event.target.value) as ShmpCourseNumber)}
              className="px-2 py-2 border border-border rounded-md bg-background text-xs"
            >
              {COURSE_OPTIONS.filter(course => course !== member.course_number).map(course => (
                <option key={course} value={course}>{course} курс</option>
              ))}
            </select>
            <select
              value={transferDate}
              onChange={event => setTransferDate(event.target.value)}
              className="px-2 py-2 border border-border rounded-md bg-background text-xs"
            >
              <option value="">Начало недели</option>
              {props.transferDates.map(week => (
                <option key={week.week_id} value={week.week_start}>
                  {week.week_start} - {week.week_end}
                </option>
              ))}
            </select>
            <input
              value={transferReason}
              onChange={event => setTransferReason(event.target.value)}
              placeholder="Причина перевода"
              className="px-2 py-2 border border-border rounded-md bg-background text-xs"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={props.busy || !transferDate}
              onClick={() => props.onTransfer(member, newCourse, transferDate, transferReason)}
            >
              Перевести
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={props.busy}
              onClick={() => setShowExclude(current => !current)}
            >
              Исключить
            </Button>
          </div>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => props.onOpenAnalytics(member.partner_number)}
        >
          Аналитика
        </Button>
      </div>

      {props.isAdvanced && showExclude && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/5 p-3">
          <div className="text-sm font-medium">Исключение из ШМП</div>
          <div className="text-xs text-muted-foreground mt-1">
            Укажите последний день участия. Все более поздние переводы будут отменены.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[auto_minmax(0,1fr)_auto] gap-2 mt-2">
            <input
              type="date"
              min={member.enrolled_from}
              max={member.enrolled_to ?? undefined}
              value={excludeDate}
              onChange={event => setExcludeDate(event.target.value)}
              className="px-2 py-2 border border-border rounded-md bg-background text-xs"
            />
            <input
              value={excludeReason}
              onChange={event => setExcludeReason(event.target.value)}
              placeholder="Причина исключения"
              className="px-2 py-2 border border-border rounded-md bg-background text-xs"
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={props.busy || !excludeDate || !excludeReason.trim()}
              onClick={async () => {
                const confirmed = window.confirm(
                  `Исключить франчайзи ${member.partner_number} после ${excludeDate}?`
                )
                if (!confirmed) return
                await props.onExclude(member, excludeDate, excludeReason)
                setShowExclude(false)
              }}
            >
              Подтвердить
            </Button>
          </div>
        </div>
      )}

      <button
        onClick={() => props.onToggleHistory(member.partner_number)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-3"
      >
        {props.expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        История курсов
      </button>

      {props.expanded && (
        <div className="mt-2 border-t border-border pt-2 space-y-1">
          {props.history.map(row => (
            <div key={row.membership_id} className="text-xs text-muted-foreground">
              {row.course_number} курс: {row.enrolled_from} - {row.enrolled_to ?? 'по настоящее время'}
              {row.change_reason ? ` · вход: ${row.change_reason}` : ''}
              {row.closed_reason ? ` · выход: ${row.closed_reason}` : ''}
              {row.canceled_at ? ` · отменено${row.cancel_reason ? `: ${row.cancel_reason}` : ''}` : ''}
            </div>
          ))}
          {props.history.length === 0 && (
            <div className="text-xs text-muted-foreground">История загружается...</div>
          )}
        </div>
      )}
    </Card>
  )
}
